import { getTierThreshold, getTiersAbove } from 'loyalty/selectors/getLoyaltyTierThresholds'
import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import { API_CALL_FAILURE, API_CALL_REQUEST, API_CALL_SUCCESS, CLEAR_LOYALTY_ENROLMENT, UPDATE_LOYALTY_ESIM_CODE, SET_LOYALTY_ESIM_BENEFIT_LIST, DEV_OVERRIDE_LOYALTY_STATE, SET_SEARCH_BURN_ENABLED, CLEAR_SEARCH_BURN_BATCH_CALCULATIONS, SET_BUNDLED_BURN_BATCH_RESULT } from 'actions/actionConstants'
import {
  USER_LOGOUT,
  ENROL_LOYALTY_ACCOUNT,
  FETCH_LOYALTY_ACCOUNT,
  FETCH_LOYALTY_ACCOUNT_ELIGIBILITY,
  FETCH_LOYALTY_POINTS_EARN_CALCULATION,
  FETCH_LOYALTY_POINTS_EARN_CALCULATION_BATCH,
  SUBMIT_LOYALTY_POINTS_BURN_CALCULATION,
  FETCH_LOYALTY_POINTS_BURN_BATCH_CALCULATION,
  FETCH_LOYALTY_CONFIG,
  FETCH_LOYALTY_ACTIVE_CUSTOM_RULES,
  FETCH_LOYALTY_ACCOUNT_ACTIVITY,
  FETCH_LOYALTY_ACCOUNT_PENDING_POINTS,
  FETCH_LATEST_LOYALTY_ACCOUNT_DETAILS,
  FETCH_LOYALTY_PURCHASE_POINTS_OFFERS,
} from 'actions/apiActionConstants'
import moment from 'moment'

export const INITIAL_ESIM_PROMO_CODES_STATE: App.LoyaltyEsimPromoCodesState = {
  codesByOrderId: {},
  benefitOrderIds: [],
  benefitListLoaded: false,
}

export const INITIAL_LOYALTY_STATE: App.LoyaltyState = {
  enrolment: undefined,
  account: { initial: true },
  promoCampaigns: [],
  eligibilityTier: { initial: true },
  pointsEarnCalculations: {},
  pointsBurnCalculations: {},
  pointsBurnBatchCalculations: {},
  config: undefined,
  configError: undefined,
  configErrorRegion: undefined,
  fetchingConfig: false,
  activeCustomRules: undefined,
  activeCustomRulesError: undefined,
  fetchingActiveCustomRules: false,
  accountActivity: { initial: true },
  accountPendingPoints: { initial: true },
  esimPromoCodes: INITIAL_ESIM_PROMO_CODES_STATE,
  hasSelectedBurnToggle: false,
  purchaseOffers: {},
}

const apiCalls = apiReducerSwitch<App.LoyaltyState>({
  [ENROL_LOYALTY_ACCOUNT]: {
    request: () => ({
      account: {
        fetching: true,
        error: undefined,
      },
    }),
    success: (state, action) => ({
      account: {
        data: action.data?.account,
        fetching: false,
        error: undefined,
      },
      promoCampaigns: action.data?.promoCampaigns.length ? action.data.promoCampaigns : state.promoCampaigns,
      enrolment: action.enrolment,
    }),
    failure: (_state, action) => ({
      account: {
        fetching: false,
        error: action.error,
      },
    }),
  },
  [FETCH_LOYALTY_ACCOUNT_ACTIVITY]: {
    request: (state) => ({
      accountActivity: {
        data: state.accountActivity.data,
        fetching: true,
        error: undefined,
      },
    }),
    success: (state, action) => ({
      accountActivity: {
        data: {
          activities: action.page === 1
            ? action.data.activities
            : [
                ...(state.accountActivity.data?.activities ?? []),
                ...action.data.activities,
              ],
          hasNextPage: action.data.hasNextPage,
          nextAbsoluteIndex: action.data.nextAbsoluteIndex,
        },
        fetching: false,
        error: undefined,
      },
    }),
    failure: (state, action) => ({
      accountActivity: {
        data: state.accountActivity.data,
        fetching: false,
        error: action.error,
      },
    }),
  },
  [FETCH_LOYALTY_ACCOUNT_PENDING_POINTS]: {
    request: () => ({
      accountPendingPoints: {
        fetching: true,
        error: undefined,
      },
    }),
    success: (_state, action) => ({
      accountPendingPoints: {
        data: action.data,
        fetching: false,
        error: undefined,
      },
    }),
    failure: (_state, action) => ({
      accountPendingPoints: {
        fetching: false,
        error: action.error,
      },
    }),
  },
  [FETCH_LOYALTY_ACCOUNT]: {
    request: (state) => state.devOverrideActive ? state : ({
      account: {
        fetching: true,
        error: undefined,
      },
    }),
    success: (state, action) => state.devOverrideActive ? state : ({
      account: {
        data: action.data?.account ? {
          ...action.data.account,
          fetchedAt: moment().utc().toISOString(),
        } : undefined,
        fetching: false,
        error: undefined,
      },
      promoCampaigns: action.data?.promoCampaigns ?? [],
    }),
    failure: (state, action) => state.devOverrideActive ? state : ({
      account: {
        fetching: false,
        error: action.error,
      },
    }),
  },
  [FETCH_LOYALTY_ACCOUNT_ELIGIBILITY]: {
    request: (state) => state.devOverrideActive ? state : ({
      eligibilityTier: {
        fetching: true,
        error: undefined,
      },
    }),
    success: (state, action) => state.devOverrideActive ? state : ({
      eligibilityTier: {
        data: action.data,
        fetching: false,
        error: undefined,
      },
    }),
    failure: (state, action) => state.devOverrideActive ? state : ({
      eligibilityTier: {
        fetching: false,
        error: action.error,
      },
    }),
  },
  [FETCH_LOYALTY_POINTS_EARN_CALCULATION]: {
    request: (state, action) => ({
      pointsEarnCalculations: {
        ...state.pointsEarnCalculations,
        [action.pointsEarnCalculationKey]: { fetching: true },
      },
    }),
    success: (state, action) => ({
      pointsEarnCalculations: {
        ...state.pointsEarnCalculations,
        [action.pointsEarnCalculationKey]: { data: action.data, fetching: false },
      },
    }),
    failure: (state, action) => ({
      pointsEarnCalculations: {
        ...state.pointsEarnCalculations,
        [action.pointsEarnCalculationKey]: { error: action.error, fetching: false },
      },
    }),
  },
  [FETCH_LOYALTY_POINTS_EARN_CALCULATION_BATCH]: {
    request: (state, action) => {
      const updates: Record<string, App.StatefulData<App.LoyaltyPointsEarnCalculation>> = {}
      for (const key of action.pointsEarnCalculationKeys) {
        updates[key] = { fetching: true }
      }
      return {
        pointsEarnCalculations: {
          ...state.pointsEarnCalculations,
          ...updates,
        },
      }
    },
    success: (state, action) => {
      const results = action.data
      const updates: Record<string, App.StatefulData<App.LoyaltyPointsEarnCalculation>> = {}
      action.pointsEarnCalculationKeys.forEach((key: string, index: number) => {
        const result = results[index]
        if (result) {
          updates[key] = { data: result, fetching: false }
        } else {
          updates[key] = { error: 'Calculation failed', fetching: false }
        }
      })
      return {
        pointsEarnCalculations: {
          ...state.pointsEarnCalculations,
          ...updates,
        },
      }
    },
    failure: (state, action) => {
      const updates: Record<string, App.StatefulData<App.LoyaltyPointsEarnCalculation>> = {}
      for (const key of action.pointsEarnCalculationKeys) {
        updates[key] = { error: action.error, fetching: false }
      }
      return {
        pointsEarnCalculations: {
          ...state.pointsEarnCalculations,
          ...updates,
        },
      }
    },
  },
  [SUBMIT_LOYALTY_POINTS_BURN_CALCULATION]: {
    request: (state, action) => {
      return {
        pointsBurnCalculations: {
          ...state.pointsBurnCalculations,
          [action.pointsBurnCalculationKey]: { fetching: true },
        },
      }
    },
    success: (state, action) => {
      return {
        pointsBurnCalculations: {
          ...state.pointsBurnCalculations,
          [action.pointsBurnCalculationKey]: { data: action.data, fetching: false },
        },
      }
    },
    failure: (state, action) => {
      return {
        pointsBurnCalculations: {
          ...state.pointsBurnCalculations,
          [action.pointsBurnCalculationKey]: { error: action.error, fetching: false },
        },
      }
    },
  },
  [FETCH_LOYALTY_POINTS_BURN_BATCH_CALCULATION]: {
    request: (state, action) => ({
      pointsBurnBatchCalculations: {
        ...state.pointsBurnBatchCalculations,
        [action.burnBatchKey]: { fetching: true },
      },
    }),
    success: (state, action) => ({
      pointsBurnBatchCalculations: {
        ...state.pointsBurnBatchCalculations,
        [action.burnBatchKey]: { data: action.data, fetching: false },
      },
    }),
    failure: (state, action) => ({
      pointsBurnBatchCalculations: {
        ...state.pointsBurnBatchCalculations,
        [action.burnBatchKey]: { error: action.error, fetching: false },
      },
    }),
  },
  [FETCH_LOYALTY_CONFIG]: {
    request: () => {
      return {
        fetchingConfig: true,
      }
    },
    success: (state, action) => {
      return {
        config: action.data,
        fetchingConfig: false,
        configError: undefined,
        configErrorRegion: undefined,
      }
    },
    failure: (state, action) => {
      return {
        configError: action.error,
        configErrorRegion: action.region,
        fetchingConfig: false,
      }
    },
  },
  [FETCH_LOYALTY_ACTIVE_CUSTOM_RULES]: {
    request: () => {
      return {
        fetchingActiveCustomRules: true,
      }
    },
    success: (state, action) => {
      return {
        activeCustomRules: action.data,
        fetchingActiveCustomRules: false,
      }
    },
    failure: (state, action) => {
      return {
        activeCustomRulesError: action.error,
        fetchingActiveCustomRules: false,
      }
    },
  },
  [USER_LOGOUT]: {
    success: () => INITIAL_LOYALTY_STATE,
  },
  [FETCH_LATEST_LOYALTY_ACCOUNT_DETAILS]: {
    success: (state, action) => {
      if (state.devOverrideActive) return state
      return {
        account: {
          data: action.data?.account ? {
            ...action.data.account,
            fetchedAt: moment().utc().toISOString(),
          } : undefined,
          fetching: false,
        },
        promoCampaigns: action.data?.promoCampaigns ?? [],
      }
    },
  },
  [FETCH_LOYALTY_PURCHASE_POINTS_OFFERS]: {
    request: (state, action) => ({
      purchaseOffers: {
        ...state.purchaseOffers,
        [action.key]: { fetching: true, data: state.purchaseOffers[action.key]?.data },
      },
    }),
    success: (state, action) => ({
      purchaseOffers: {
        ...state.purchaseOffers,
        [action.key]: { fetching: false, data: action.data },
      },
    }),
    failure: (state, action) => ({
      purchaseOffers: {
        ...state.purchaseOffers,
        [action.key]: { fetching: false, error: action.error },
      },
    }),
  },
})

function tierBenefitToAccountBenefit(
  tierBenefit: App.LoyaltyRegionTierBenefit,
): App.LoyaltyBenefit {
  return {
    code: tierBenefit.code,
    description: tierBenefit.code.replace(/_/g, ' '),
    remaining: tierBenefit.unlimited ? undefined : tierBenefit.limit,
    limit: tierBenefit.limit,
    unlimited: tierBenefit.unlimited,
    value: tierBenefit.value,
  }
}

function computeTierThresholds(
  tier: App.LoyaltyTier,
  thresholds: Record<App.LoyaltyTier, App.LoyaltyTierThreshold> | undefined,
) {
  if (!thresholds) return {}

  const currentThreshold = getTierThreshold(thresholds, tier)
  const [nextTier] = getTiersAbove(tier)
  const nextThreshold = nextTier ? getTierThreshold(thresholds, nextTier) : undefined

  return {
    totalCreditsForTierRetention: currentThreshold?.statusCredits ?? 0,
    numberOfNightsForTierRetention: currentThreshold?.nights ?? 0,
    totalCreditsForTierUpgrade: nextThreshold?.statusCredits ?? 0,
    numberOfNightsForTierUpgrade: nextThreshold?.nights ?? 0,
  }
}

const DEFAULT_LOYALTY_ACCOUNT: App.LoyaltyAccount = {
  accountNumber: 0,
  points: 0,
  tier: 'bronze',
  statusCredits: 0,
  ibsMemberId: '',
  totalCreditsForTierUpgrade: 0,
  totalCreditsForTierRetention: 0,
  numberOfNights: 0,
  numberOfNightsForTierUpgrade: 0,
  numberOfNightsForTierRetention: 0,
  benefits: [],
  promoCampaigns: [],
  region: 'AU',
}

export default createAppReducer<App.LoyaltyState>(INITIAL_LOYALTY_STATE, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [CLEAR_LOYALTY_ENROLMENT]: () => ({
    enrolment: undefined,
  }),
  [UPDATE_LOYALTY_ESIM_CODE]: (state, action) => ({
    esimPromoCodes: {
      ...state.esimPromoCodes,
      codesByOrderId: {
        ...state.esimPromoCodes.codesByOrderId,
        [action.orderId]: {
          ...state.esimPromoCodes.codesByOrderId[action.orderId],
          ...action.update,
        },
      },
    },
  }),
  [SET_LOYALTY_ESIM_BENEFIT_LIST]: (state, action) => ({
    esimPromoCodes: {
      ...state.esimPromoCodes,
      benefitOrderIds: action.orderIds,
      benefitListLoaded: true,
    },
  }),
  [SET_SEARCH_BURN_ENABLED]: (_state, action) => ({
    hasSelectedBurnToggle: action.enabled,
  }),
  [CLEAR_SEARCH_BURN_BATCH_CALCULATIONS]: () => ({
    pointsBurnBatchCalculations: {},
  }),
  [SET_BUNDLED_BURN_BATCH_RESULT]: (state, action) => ({
    pointsBurnBatchCalculations: {
      ...state.pointsBurnBatchCalculations,
      [action.burnBatchKey]: {
        data: {
          calculationKey: action.burnBatchKey,
          priceInPoints: action.priceInPoints,
          isRedeemableWithPoints: true,
        },
        fetching: false,
      },
    },
  }),
  [DEV_OVERRIDE_LOYALTY_STATE]: (state, action) => {
    const { overrides } = action

    if (overrides.hasAccount === false) {
      return {
        devOverrideActive: true,
        account: { data: undefined, fetching: false, error: undefined },
        eligibilityTier: { data: 'bronze' as App.LoyaltyTier, fetching: false, error: undefined },
      }
    }

    const currentData = state.account.data ?? DEFAULT_LOYALTY_ACCOUNT
    const tier = overrides.tier ?? currentData.tier
    const tierBenefits = state.config?.regionTierBenefits[tier]
    const benefits = tierBenefits
      ? tierBenefits.map(tierBenefitToAccountBenefit)
      : currentData.benefits
    const tierThresholdOverrides = overrides.tier !== undefined
      ? computeTierThresholds(overrides.tier, state.config?.tierThresholds)
      : {}

    return {
      devOverrideActive: true,
      account: {
        data: {
          ...currentData,
          ...(overrides.tier !== undefined && { tier: overrides.tier }),
          ...(overrides.points !== undefined && { points: overrides.points }),
          ...(overrides.statusCredits !== undefined && { statusCredits: overrides.statusCredits }),
          ...(overrides.numberOfNights !== undefined && { numberOfNights: overrides.numberOfNights }),
          ...(overrides.statusReviewDate !== undefined && { statusReviewDate: overrides.statusReviewDate }),
          ...(overrides.region !== undefined && { region: overrides.region }),
          ...tierThresholdOverrides,
          benefits,
        },
        fetching: false,
        error: undefined,
      },
    }
  },
})
